import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './Navbar.css';

const Navbar = () => {
  const location = useLocation();
  const { isAuthenticated, logout, admin } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({}); // Track which mobile dropdowns are open
  const [translateOpen, setTranslateOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState('English');
  const [isScrolled, setIsScrolled] = useState(false);
  const isHomePage = location.pathname === '/';
  const drawerRef = useRef(null);
  const hamburgerRef = useRef(null);
  const translateRef = useRef(null);

  const indianLanguages = [
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'हिन्दी' },
    { code: 'bn', name: 'বাংলা' },
    { code: 'te', name: 'తెలుగు' },
    { code: 'mr', name: 'मराठी' },
    { code: 'ta', name: 'தமிழ்' },
    { code: 'gu', name: 'ગુજરાતી' },
    { code: 'kn', name: 'ಕನ್ನಡ' },
    { code: 'ml', name: 'മലയാളം' },
    { code: 'pa', name: 'ਪੰਜਾਬੀ' },
    { code: 'or', name: 'ଓଡ଼ିଆ' },
    { code: 'as', name: 'অসমীয়া' },
    { code: 'ur', name: 'اردو' },
    { code: 'sa', name: 'संस्कृतम्' },
    { code: 'ne', name: 'नेपाली' }
  ];

  // Initialize selected language from cookie on mount
  useEffect(() => {
    const match = document.cookie.match(/googtrans=\/en\/([^;]+)/);
    if (match && match[1]) {
      const code = match[1];
      const lang = indianLanguages.find(l => l.code === code);
      if (lang) {
        setSelectedLang(lang.name);
      }
    }
  }, []);

  const handleTranslate = (langCode, langName) => {
    setSelectedLang(langName);
    setTranslateOpen(false);

    const host = window.location.hostname;
    const isLocalhost = host === 'localhost' || host === '127.0.0.1';

    if (langCode === 'en') {
      // Expire cookie on all domain variations
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      if (!isLocalhost) {
        document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${host};`;
        document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${host};`;
      }
      window.location.reload();
      return;
    }

    const cookieValue = `/en/${langCode}`;
    // localhost doesn't accept a domain attribute — set path-only
    document.cookie = `googtrans=${cookieValue}; path=/;`;
    if (!isLocalhost) {
      // Production: must set on both bare domain AND .domain so all subdomains pick it up
      document.cookie = `googtrans=${cookieValue}; path=/; domain=${host};`;
      document.cookie = `googtrans=${cookieValue}; path=/; domain=.${host};`;
    }
    window.location.reload();
  };

  const isActive = (path) => {
    if (path.includes('#')) {
      return location.pathname + location.hash === path;
    }
    return location.pathname === path;
  };

  const navLinks = [
    {
      label: 'About',
      path: '/about',
      dropdown: [
        { label: 'Why Us', path: '/about#why-us' },
        { label: 'Our Values', path: '/about#our-values' },
      ]
    },
    {
      label: 'Our People',
      path: '/our-people',
    },
    {
      label: 'Areas of Practice',
      path: '/area-of-practice',
      dropdown: [
        { label: 'Corporate & Commercial Litigation', path: '/area-of-practice#corporate-commercial-litigation' },
        { label: 'Intellectual Property Rights (IPR)', path: '/area-of-practice#intellectual-property-rights-ipr' },
        { label: 'Criminal Litigation', path: '/area-of-practice#criminal-litigation' },
        { label: 'Family & Matrimonial Law', path: '/area-of-practice#family-matrimonial-law' },
        { label: 'Environmental Law', path: '/area-of-practice#environmental-law' },
        { label: 'Civil Litigation', path: '/area-of-practice#civil-litigation' },
        { label: 'Arbitration & ADR', path: '/area-of-practice#arbitration-adr' },
        { label: 'Real Estate & RERA', path: '/area-of-practice#real-estate-rera' },
        { label: 'Consumer Protection Law', path: '/area-of-practice#consumer-protection-law' },
        { label: 'Labour & Employment Law', path: '/area-of-practice#labour-employment-law' },
        { label: 'Banking, Finance & Debt Recovery', path: '/area-of-practice#banking-finance-debt-recovery' },
        { label: 'Service & Administrative Law', path: '/area-of-practice#service-administrative-law' },
        { label: 'Startup, MSME & Business Advisory', path: '/area-of-practice#startup-msme-business-advisory' },
        { label: 'Legal Documentation & Drafting', path: '/area-of-practice#legal-documentation-drafting' },
      ]
    },
    {
      label: 'Insights',
      path: '/insights',
      dropdown: [
        { label: 'All Insights', path: '/insights' },
        { label: 'Articles', path: '/insights?category=article' },
        { label: 'News', path: '/insights?category=news' },
        { label: 'Blog', path: '/insights?category=blog' },
      ]
    },
  ];

  const toggleDropdown = (label, e) => {
    if (e) e.preventDefault();
    setOpenDropdowns(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  // Close drawer on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Close drawer on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuOpen &&
        drawerRef.current && !drawerRef.current.contains(e.target) &&
        hamburgerRef.current && !hamburgerRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
      }
      if (
        translateOpen &&
        translateRef.current && !translateRef.current.contains(e.target)
      ) {
        setTranslateOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Google Translate Integration with Error Handling
  useEffect(() => {
    // Inject CSS to permanently suppress the Google Translate top banner
    // and the body offset it causes — must run before the script loads
    if (!document.getElementById('goog-translate-suppress')) {
      const style = document.createElement('style');
      style.id = 'goog-translate-suppress';
      style.textContent = `
        /* Hide the Google Translate banner iframe */
        .goog-te-banner-frame,
        #goog-te-banner-frame,
        .skiptranslate > iframe {
          display: none !important;
          visibility: hidden !important;
          height: 0 !important;
        }
        /* Reset the body top offset Google Translate injects */
        body {
          top: 0 !important;
          position: static !important;
        }
        /* Hide the "Translated by Google" widget if it appears */
        .goog-te-gadget-simple,
        .goog-logo-link,
        .goog-te-menu-value span:last-child {
          display: none !important;
        }
      `;
      document.head.appendChild(style);
    }

    // Prevent multiple script injections if navigating back and forth
    if (document.querySelector('script[src*="translate.google.com"]')) {
      return;
    }

    const loadGoogleTranslate = () => {
      if (document.querySelector('script[src*="translate.google.com"]')) return;
      const addScript = document.createElement('script');
      addScript.setAttribute('src', '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit');
      addScript.defer = true;
      document.body.appendChild(addScript);

      window.googleTranslateElementInit = () => {
        try {
          new window.google.translate.TranslateElement(
            {
              pageLanguage: 'en',
              includedLanguages: 'hi,bn,te,mr,ta,gu,kn,ml,pa,or,as,ur,sa,ne',
              layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
              autoDisplay: false,
            },
            'google_translate_element'
          );
        } catch (error) {
          console.error("Error initializing Google Translate:", error);
        }
      };
    };

    // Lazily load Google Translate to prevent main-thread blockage
    const translateTimeout = setTimeout(loadGoogleTranslate, 3500);

    // MutationObserver: Google Translate injects body{top:Xpx} after init.
    // Watch for that and reset it immediately every time it changes.
    const observer = new MutationObserver(() => {
      if (document.body.style.top && document.body.style.top !== '0px') {
        document.body.style.top = '0px';
      }
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });

    return () => {
      clearTimeout(translateTimeout);
      delete window.googleTranslateElementInit;
      observer.disconnect();
    };
  }, []);

  // Handle scroll for transparency
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header className={`top-navbar ${isHomePage && !isScrolled ? 'top-navbar--transparent' : ''}`}>
        <div className="top-navbar__inner">

          {/* ── Logo ── */}
          <Link to="/" className="top-navbar__logo">
            <div className="nav-monogram-container">
              <div className="nav-monogram-outer-ring"></div>
              <div className="nav-monogram-divider"></div>
              <span className="nav-monogram-text">EL</span>
              <div className="nav-monogram-divider"></div>
            </div>

            <svg width="100" height="40" viewBox="100 0 300 180" xmlns="http://www.w3.org/2000/svg" aria-label="EdZure Legal">
              <defs>
                <linearGradient id="gold" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#f3d38b" />
                  <stop offset="100%" stopColor="#b8860b" />
                </linearGradient>
              </defs>

              <text x="200" y="80" textAnchor="middle" fontFamily="'Playfair Display', serif" fontSize="76" fontWeight="700" fill="#ffffff">
                EDZURE
              </text>

              <text x="200" y="150" textAnchor="middle" fontFamily="'Playfair Display', serif" fontSize="56" letterSpacing="5" fill="url(#gold)">
                LEGAL
              </text>
            </svg>
          </Link>

          {/* ── Desktop nav links ── */}
          <nav className="top-navbar__links">
            {navLinks.map(({ label, path, dropdown }) => {
              if (dropdown) {
                return (
                  <div key={label} className="top-navbar__dropdown">
                    <Link
                      to={path}
                      className={`top-navbar__link top-navbar__dropdown-toggle${isActive(path) ? ' active' : ''}`}
                    >
                      {label}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '4px', verticalAlign: '-2px' }}><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </Link>
                    <div className="top-navbar__dropdown-menu">
                      {dropdown.map(dropItem => (
                        <Link key={dropItem.path} to={dropItem.path} className="top-navbar__dropdown-item">
                          {dropItem.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <Link
                  key={path}
                  to={path}
                  className={`top-navbar__link${isActive(path) ? ' active' : ''}`}
                >
                  {label}
                </Link>
              );
            })}

            <Link
              to="/#contact"
              className="top-navbar__link top-navbar__contact"
            >
              Contact Us
            </Link>

            {/* Hidden Google Translate Element Container. DO NOT use display:none or the select box won't render properly */}
            <div id="google_translate_element" style={{ width: 0, height: 0, overflow: 'hidden', position: 'absolute', opacity: 0 }}></div>

            {/* Custom Translator UI */}
            <div className="top-navbar__custom-translate" ref={translateRef}>
              <button
                className="top-navbar__translate-btn"
                onClick={() => setTranslateOpen(!translateOpen)}
                aria-label="Translate Website"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="2" y1="12" x2="22" y2="12"></line>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                </svg>
                <span className="translate-btn-text">{selectedLang}</span>
                <svg className={`translate-arrow ${translateOpen ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>

              <div className={`translate-dropdown ${translateOpen ? 'show' : ''}`}>
                <div className="translate-dropdown-inner">
                  {indianLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      className={`translate-dropdown-item ${selectedLang === lang.name ? 'active' : ''}`}
                      onClick={() => handleTranslate(lang.code, lang.name)}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {isAuthenticated && (
              <>
                <Link
                  to="/admin/dashboard"
                  className={`top-navbar__link${isActive('/admin/dashboard') ? ' active' : ''}`}
                >
                  Dashboard
                </Link>
                <button
                  className="top-navbar__link top-navbar__logout"
                  onClick={logout}
                >
                  Logout ({admin?.username})
                </button>
              </>
            )}
          </nav>

          {/* ── Hamburger button (mobile only) ── */}
          <button
            ref={hamburgerRef}
            className={`top-navbar__hamburger${menuOpen ? ' open' : ''}`}
            onClick={() => setMenuOpen(prev => !prev)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span className="top-navbar__bar" />
            <span className="top-navbar__bar" />
            <span className="top-navbar__bar" />
          </button>

        </div>
      </header>

      {/* ── Mobile drawer ── */}
      <div
        ref={drawerRef}
        className={`top-navbar__drawer${menuOpen ? ' open' : ''}`}
        aria-hidden={!menuOpen}
      >
        <div className="top-navbar__drawer-inner">

          {navLinks.map(({ label, path, dropdown }) => (
            <React.Fragment key={path}>
              {dropdown ? (
                <div
                  className={`top-navbar__drawer-link${isActive(path) ? ' active' : ''}`}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 16px' }}
                >
                  <Link
                    to={path}
                    onClick={() => setMenuOpen(false)}
                    style={{ flexGrow: 1, padding: '14px 0', textDecoration: 'none', color: 'inherit' }}
                  >
                    {label}
                  </Link>
                  <div
                    onClick={(e) => toggleDropdown(label, e)}
                    style={{ padding: '14px 0 14px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                  >
                    <svg
                      width="16" height="16" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      style={{
                        transform: openDropdowns[label] ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease'
                      }}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                </div>
              ) : (
                <Link
                  to={path}
                  className={`top-navbar__drawer-link${isActive(path) ? ' active' : ''}`}
                  onClick={() => setMenuOpen(false)}
                >
                  {label}
                </Link>
              )}

              {dropdown && openDropdowns[label] && (
                <div className="top-navbar__drawer-sublinks">
                  {dropdown.map(dropItem => (
                    <Link
                      key={dropItem.path}
                      to={dropItem.path}
                      className={`top-navbar__drawer-sublink${isActive(dropItem.path) ? ' active' : ''}`}
                      onClick={() => setMenuOpen(false)}
                    >
                      {dropItem.label}
                    </Link>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}

          <Link
            to="/#contact"
            className="top-navbar__drawer-link top-navbar__drawer-contact"
            onClick={() => setMenuOpen(false)}
          >
            Contact Us
          </Link>

          <div className="top-navbar__drawer-divider" />
          <div className="top-navbar__drawer-lang-selector">
            <p className="drawer-lang-title">Language Selector</p>
            <div className="drawer-lang-grid">
              {indianLanguages.map((lang) => (
                <button
                  key={lang.code}
                  className={`drawer-lang-btn ${selectedLang === lang.name ? 'active' : ''}`}
                  onClick={() => {
                    handleTranslate(lang.code, lang.name);
                    setMenuOpen(false);
                  }}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          </div>

          {isAuthenticated && (
            <>
              <div className="top-navbar__drawer-divider" />
              <Link
                to="/admin/dashboard"
                className={`top-navbar__drawer-link${isActive('/admin/dashboard') ? ' active' : ''}`}
                onClick={() => setMenuOpen(false)}
              >
                Dashboard
              </Link>
              <button
                className="top-navbar__drawer-link top-navbar__drawer-logout"
                onClick={() => { logout(); setMenuOpen(false); }}
              >
                Logout ({admin?.username})
              </button>
            </>
          )}

        </div>
      </div>
    </>
  );
};

export default Navbar;